import { getToday } from "../utils";
import { somarDias } from "./abordagem";

// ═══════════════════════════════════════════════════════════════
// METAS DE CONTATO — quanto foi combinado, quanto foi feito
// ═══════════════════════════════════════════════════════════════
//
// Meta é combinação e mora na tabela `metas`. Execução é fato e sai dos
// contatos registrados — que já carregam data, autor, UF e cidade. Nunca
// somamos "pontos" numa coluna: contagem derivada não desencontra do que
// aconteceu, e não tem como alguém empurrar um número.
//
// Só conta contato COM COMPROVANTE quando o canal exige. Meta que aceita
// registro sem prova ensina a inventar registro — foi para não cair nisso que
// o print virou obrigatório.

export const META_PADRAO = { contatosDia: 8, contatosSemana: 40 };

/** A meta que vale para esta pessoa: a individual, ou a geral. */
export function metaDe(metas = [], userId) {
  const lista = Array.isArray(metas) ? metas : [];
  const individual = lista.find((m) => m.id === userId && m.ativa !== false);
  const geral = lista.find((m) => m.id === "geral" && m.ativa !== false);
  const fonte = individual || geral;
  return {
    contatosDia: Number(fonte?.contatosDia ?? META_PADRAO.contatosDia) || 0,
    contatosSemana: Number(fonte?.contatosSemana ?? META_PADRAO.contatosSemana) || 0,
    individual: !!individual,
  };
}

/** Todos os contatos registrados, achatados, com de onde vieram. */
export function contatos(partners = [], { userId = null } = {}) {
  const saida = [];
  for (const p of partners || []) {
    for (const it of Array.isArray(p.interactions) ? p.interactions : []) {
      if (userId && (it.userId || p.userId) !== userId) continue;
      saida.push({
        data: String(it.date || "").slice(0, 10),
        autor: it.autor || "",
        canal: it.channel || "",
        comprovado: !!it.comprovantePath,
        // A região vem gravada no contato, não lida do parceiro na hora do
        // relatório: se a empresa mudar de endereço depois, o contato de hoje
        // continua tendo acontecido onde aconteceu.
        uf: it.uf || p.region || "",
        cidade: it.cidade || "",
        empresa: p.name || "",
        parceiroId: p.id,
        diaDaConversa: it.diaDaConversa || "",
        diasDeAtraso: Number(it.diasDeAtraso || 0),
      });
    }
  }
  return saida;
}

export const contatosNoDia = (lista, dia) => lista.filter((c) => c.data === dia);

/**
 * Contatos registrados com atraso — a conversa é de um dia, o lançamento é de
 * outro. NÃO muda a meta: o dia de trabalho medido é o do registro. Serve para
 * a gerência saber quando o número de hoje é, na verdade, trabalho da semana
 * passada sendo lançado de uma vez.
 */
export const ATRASO_MINIMO_DIAS = 1;

export function contatosComAtraso(lista = [], minimo = ATRASO_MINIMO_DIAS) {
  return lista.filter((c) => Number(c.diasDeAtraso || 0) >= minimo);
}

export function resumoAtraso(lista = []) {
  const atrasados = contatosComAtraso(lista);
  if (!atrasados.length) return { total: 0, pior: 0, media: 0 };
  const dias = atrasados.map((c) => Number(c.diasDeAtraso || 0));
  return {
    total: atrasados.length,
    pior: Math.max(...dias),
    media: Math.round(dias.reduce((s, d) => s + d, 0) / dias.length),
  };
}

export function contatosNaSemana(lista, hoje = getToday()) {
  // Semana corrida de 7 dias terminando hoje. Semana "de segunda" puniria quem
  // trabalha sábado e premiaria quem some na segunda.
  const inicio = somarDias(hoje, -6);
  return lista.filter((c) => c.data >= inicio && c.data <= hoje);
}

/** Como está o dia: feito, meta, e o que falta. */
export function progressoDoDia(partners, userId, metas, hoje = getToday()) {
  const meta = metaDe(metas, userId);
  const lista = contatos(partners, { userId });
  const hojeLista = contatosNoDia(lista, hoje);
  const feitos = hojeLista.length;
  const comProva = hojeLista.filter((c) => c.comprovado).length;
  const semana = contatosNaSemana(lista, hoje).length;
  return {
    meta: meta.contatosDia,
    metaSemana: meta.contatosSemana,
    individual: meta.individual,
    feitos, comProva, semana,
    faltam: Math.max(0, meta.contatosDia - feitos),
    bateu: meta.contatosDia > 0 && feitos >= meta.contatosDia,
    percentual: meta.contatosDia > 0 ? Math.min(100, Math.round((feitos / meta.contatosDia) * 100)) : 0,
  };
}

/**
 * Os dias úteis recentes em que a meta não foi batida. É o que a cobrança
 * mostra — e ela olha para trás, nunca para o dia de hoje, que ainda está
 * acontecendo.
 */
export function diasEmDebito(partners, userId, metas, hoje = getToday(), dias = 7) {
  const meta = metaDe(metas, userId);
  if (!meta.contatosDia) return [];
  const lista = contatos(partners, { userId });
  const debitos = [];
  for (let i = 1; i <= dias; i++) {
    const dia = somarDias(hoje, -i);
    const d = new Date(`${dia}T12:00:00Z`).getUTCDay();
    if (d === 0 || d === 6) continue; // fim de semana não cobra
    const feitos = contatosNoDia(lista, dia).length;
    if (feitos < meta.contatosDia) debitos.push({ dia, feitos, meta: meta.contatosDia, faltaram: meta.contatosDia - feitos });
  }
  return debitos;
}

// ── Onde a equipe está batendo ───────────────────────────────────
//
// Primeiro a região (UF), depois a cidade dentro dela. É o que responde
// "estamos concentrados demais em SP?" e "que cidade do interior nunca
// recebeu uma ligação?".

export function porRegiao(lista = []) {
  const mapa = new Map();
  for (const c of lista) {
    const uf = c.uf || "—";
    if (!mapa.has(uf)) mapa.set(uf, { uf, total: 0, comProva: 0, cidades: new Map(), empresas: new Set() });
    const r = mapa.get(uf);
    r.total += 1;
    if (c.comprovado) r.comProva += 1;
    r.empresas.add(c.parceiroId);
    const cid = c.cidade || "—";
    r.cidades.set(cid, (r.cidades.get(cid) || 0) + 1);
  }
  return [...mapa.values()]
    .map((r) => ({
      ...r,
      empresas: r.empresas.size,
      cidades: [...r.cidades.entries()].map(([cidade, total]) => ({ cidade, total })).sort((a, b) => b.total - a.total),
    }))
    .sort((a, b) => b.total - a.total);
}

/** Ranking simples por pessoa — o que a gerência olha no fim do dia. */
export function porPessoa(partners = [], metas = [], hoje = getToday()) {
  const lista = contatos(partners);
  const mapa = new Map();
  for (const c of lista) {
    const chave = c.autor || "—";
    if (!mapa.has(chave)) mapa.set(chave, { autor: chave, hoje: 0, semana: 0, comProva: 0, total: 0 });
    const r = mapa.get(chave);
    r.total += 1;
    if (c.comprovado) r.comProva += 1;
    if (c.data === hoje) r.hoje += 1;
    if (c.data >= somarDias(hoje, -6) && c.data <= hoje) r.semana += 1;
  }
  return [...mapa.values()].sort((a, b) => b.semana - a.semana);
}
